import clsx from 'clsx';
import { useState } from 'react';

import { Highlight } from '../Highlight';
import { parseStructured } from '../parse/structuredParse';
import { CellRenderer, CellRenderProps, CellValue } from '../types';

import { DocumentFileLink, isViewableDocument } from './DocumentFileLink';
import { looksLikeMarkdown, MarkdownBlock } from './markdown';
import styles from './structured.module.scss';

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

// A structured cell is an object, or an array that isn't a conversation (those are
// claimed earlier in the registry). Primitives and unparseable strings are not.
const asStructured = (value: CellValue): Record<string, unknown> | unknown[] | null => {
  const parsed = parseStructured(value);
  if (!parsed.ok) return null;
  if (isPlainObject(parsed.data)) return parsed.data;
  if (Array.isArray(parsed.data)) return parsed.data;
  return null;
};

// Looks like an object/array literal even if it won't parse — e.g. a Python dict
// with True/None that defeats JSON and JSON5. These still belong to this renderer,
// which shows them as raw text with a "couldn't parse" hint (never throws).
const looksStructured = (v: CellValue): boolean =>
  asStructured(v) !== null || (typeof v === 'string' && /^\s*[{[]/.test(v));

const detect = (samples: CellValue[]): boolean => samples.length > 0 && samples.every(looksStructured);

const rawText = (value: CellValue): string => (typeof value === 'string' ? value : JSON.stringify(value));

const entriesOf = (data: Record<string, unknown> | unknown[]): [string, unknown][] =>
  Array.isArray(data) ? data.map((v, i) => [String(i), v]) : Object.entries(data);

const previewScalar = (v: unknown): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return Array.isArray(v) ? `[${v.length}]` : `{ ${Object.keys(v).length} }`;
  return String(v);
};

const ValueNode = ({ value }: { value: unknown }) => {
  const [open, setOpen] = useState(false);
  if (value === null || value === undefined) return <span className={styles.empty}>—</span>;

  if (typeof value === 'object') {
    const nested = value as Record<string, unknown> | unknown[];
    const label = Array.isArray(nested) ? `[ ${nested.length} items ]` : `{ ${Object.keys(nested).length} fields }`;
    return (
      <div className={styles.nested}>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setOpen(o => !o)}
        >
          {open ? '▾' : '▸'} {label}
        </button>
        {/* ValueNode and KeyValue are mutually recursive to render nested structures. */}
        {/* eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define */}
        {open && <KeyValue data={nested} />}
      </div>
    );
  }

  if (typeof value === 'string' && looksLikeMarkdown(value)) return <MarkdownBlock value={value} />;
  const text = String(value);
  return (
    <>
      <Highlight text={text} />
      {typeof value === 'string' && isViewableDocument(text) && <DocumentFileLink filename={text} />}
    </>
  );
};

// Mutually recursive with ValueNode to render nested structures.
const KeyValue = ({ data }: { data: Record<string, unknown> | unknown[] }) => (
  <dl className={styles.kv}>
    {entriesOf(data).map(([key, value]) => (
      <div
        key={key}
        className={styles.row}
      >
        <dt className={styles.key}>{key}</dt>
        <dd className={styles.val}>
          <ValueNode value={value} />
        </dd>
      </div>
    ))}
  </dl>
);

const Unparseable = ({ value }: CellRenderProps) => (
  <div className={styles.unparseable}>
    <span className={styles.hint}>couldn’t parse</span>
    <span className={styles.rawInline}>
      <Highlight text={rawText(value)} />
    </span>
  </div>
);

const Compact = ({ value }: CellRenderProps) => {
  const data = asStructured(value);
  if (!data) return <span className={styles.compact}>{rawText(value)}</span>;
  const entries = entriesOf(data);
  const count = entries.length;
  const keyPreview = Array.isArray(data)
    ? entries.map(([, v]) => previewScalar(v)).join(', ')
    : entries.map(([k]) => k).join(', ');
  return (
    <div className={styles.compactWrap}>
      <span className={styles.kind}>{Array.isArray(data) ? `${count} items` : `${count} fields`}</span>
      <span className={clsx(styles.peek, styles.mono)}>{keyPreview}</span>
    </div>
  );
};

const Full = ({ value }: CellRenderProps) => {
  const data = asStructured(value);
  if (!data) return <Unparseable value={value} />;
  return <KeyValue data={data} />;
};

export const structuredRenderer: CellRenderer = {
  kind: 'structured',
  detect,
  Compact,
  Expanded: Full,
  Full,
};
